import { type RefObject, useEffect, useRef, useState } from "react";

import {
  BaselineSeries,
  ColorType,
  createChart,
  type IChartApi,
  type ISeriesApi,
  LineStyle,
  type MouseEventParams,
  type UTCTimestamp,
} from "lightweight-charts";

import type { PortfolioHistoryChartPoint } from "~/v3-poc/features/portfolio-history/portfolioHistory";
import type { AtdTheme } from "~/v3-poc/features/user-preferences";
import { type Direction, directionOf } from "../overview.format";

export interface UseOverviewAccountChartControllerParams {
  data: PortfolioHistoryChartPoint[];
  baseline?: number;
  isLoading: boolean;
  hasError: boolean;
  theme: AtdTheme;
}

export interface OverviewAccountChartHoveredPoint {
  time: number;
  value: number;
  left: number;
  tooltipWidth: number;
}

export interface OverviewAccountChartControllerValue {
  containerRef: RefObject<HTMLDivElement>;
  hovered: OverviewAccountChartHoveredPoint | null;
  isLoading: boolean;
  hasError: boolean;
  hasInsufficientData: boolean;
}

const TOOLTIP_WIDTH = 140;

const readColor = (styles: CSSStyleDeclaration, property: string) =>
  styles.getPropertyValue(property).trim();

const toSeriesData = (data: readonly PortfolioHistoryChartPoint[]) =>
  data.map(({ time, value }) => ({ time: time as UTCTimestamp, value }));

const DIRECTION_TOKENS: Record<Direction, { line: string; fill: string }> = {
  up: { line: "--green-9", fill: "--green-a4" },
  down: { line: "--red-9", fill: "--red-a4" },
  flat: { line: "--gray-12", fill: "--gray-a4" },
};

const directionOfSeries = (
  data: readonly PortfolioHistoryChartPoint[],
  baseline: number | undefined,
): Direction => {
  const start = baseline ?? data[0]?.value;
  const end = data[data.length - 1]?.value;
  if (start === undefined || end === undefined) {
    return "flat";
  }
  return directionOf(end - start);
};

const seriesPaint = (element: HTMLElement, direction: Direction) => {
  const styles = getComputedStyle(element);
  const line = readColor(styles, DIRECTION_TOKENS[direction].line);
  const fill = readColor(styles, DIRECTION_TOKENS[direction].fill);
  return {
    topLineColor: line,
    bottomLineColor: line,
    topFillColor1: fill,
    bottomFillColor1: fill,
    topFillColor2: "transparent",
    bottomFillColor2: "transparent",
  };
};

const chartThemeOptions = (element: HTMLElement) => {
  const styles = getComputedStyle(element);
  return {
    layout: {
      attributionLogo: false,
      background: { type: ColorType.Solid, color: "transparent" },
      fontFamily: readColor(styles, "--default-font-family"),
      textColor: readColor(styles, "--gray-11"),
    },
    crosshair: {
      horzLine: { visible: false },
      vertLine: {
        color: readColor(styles, "--gray-a7"),
        labelVisible: false,
        style: LineStyle.Dashed,
        width: 1 as const,
      },
    },
  };
};

interface ChartRuntime {
  chart: IChartApi;
  series: ISeriesApi<"Baseline">;
}

export const useOverviewAccountChartController = ({
  data,
  baseline,
  isLoading,
  hasError,
  theme,
}: UseOverviewAccountChartControllerParams): OverviewAccountChartControllerValue => {
  const containerRef = useRef<HTMLDivElement>(null);
  const runtimeRef = useRef<ChartRuntime | null>(null);
  const [hovered, setHovered] =
    useState<OverviewAccountChartHoveredPoint | null>(null);
  const shouldRenderChart = !isLoading && !hasError && data.length > 1;
  const hasInsufficientData = data.length <= 1;
  const direction = directionOfSeries(data, baseline);

  useEffect(() => {
    const container = containerRef.current;
    if (!shouldRenderChart || !container || runtimeRef.current) {
      return;
    }

    const chart = createChart(container, {
      autoSize: true,
      ...chartThemeOptions(container),
      grid: {
        horzLines: { visible: false },
        vertLines: { visible: false },
      },
      handleScroll: false,
      handleScale: false,
      leftPriceScale: { visible: false },
      rightPriceScale: { visible: false },
      timeScale: { visible: false, borderVisible: false },
    });
    const series = chart.addSeries(BaselineSeries, {
      baseValue: { type: "price", price: 0 },
      ...seriesPaint(container, "flat"),
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: false,
    });

    const handleCrosshairMove = (param: MouseEventParams) => {
      const point = param.seriesData.get(series);
      if (!param.point || param.time === undefined || !point) {
        setHovered(null);
        return;
      }
      if (!("value" in point)) {
        setHovered(null);
        return;
      }

      const plotWidth = container.clientWidth;
      setHovered({
        time: Number(param.time),
        value: point.value,
        left: Math.min(
          Math.max(param.point.x - TOOLTIP_WIDTH / 2, 0),
          Math.max(plotWidth - TOOLTIP_WIDTH, 0),
        ),
        tooltipWidth: TOOLTIP_WIDTH,
      });
    };
    chart.subscribeCrosshairMove(handleCrosshairMove);
    runtimeRef.current = { chart, series };

    return () => {
      runtimeRef.current = null;
      setHovered(null);
      chart.unsubscribeCrosshairMove(handleCrosshairMove);
      chart.remove();
    };
  }, [shouldRenderChart]);

  useEffect(() => {
    const runtime = runtimeRef.current;
    const container = containerRef.current;
    if (!runtime || !container) {
      return;
    }
    setHovered(null);
    runtime.series.applyOptions({
      baseValue: { type: "price", price: baseline ?? data[0]?.value ?? 0 },
      ...seriesPaint(container, direction),
    });
    runtime.series.setData(toSeriesData(data));
    runtime.chart.timeScale().fitContent();
  }, [data, baseline, direction]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: theme triggers a re-read of inherited Radix CSS variables; direction keeps series paint current.
  useEffect(() => {
    const container = containerRef.current;
    const runtime = runtimeRef.current;
    if (!container || !runtime) {
      return;
    }

    runtime.chart.applyOptions(chartThemeOptions(container));
    runtime.series.applyOptions(seriesPaint(container, direction));
  }, [theme, direction]);

  return {
    containerRef,
    hovered,
    isLoading,
    hasError,
    hasInsufficientData,
  };
};
